import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import Cookies from "js-cookie";

import "primeicons/primeicons.css";
import {useNavigate} from "react-router-dom";

import {Toolbar} from "primereact/toolbar";
import {Button} from "primereact/button";
import {Avatar} from "primereact/avatar";
import {OverlayPanel} from "primereact/overlaypanel";
import {Divider} from "primereact/divider";
import {Tag} from "primereact/tag";

import {Menubar} from "primereact/menubar";
import {Sidebar} from "primereact/sidebar";
import {PanelMenu} from "primereact/panelmenu";

import {ReactComponent as BalanceIcon} from "../Assets/balanceSheet.svg";
import {ReactComponent as CashBook} from "../Assets/cashBook.svg";
import {ReactComponent as Inventory} from "../Assets/inventory.svg";
import {ReactComponent as Ledger} from "../Assets/ledger.svg";
import {ReactComponent as Stock} from "../Assets/stock.svg";
import {ReactComponent as Purchase} from "../Assets/purchase.svg";

import {ReactComponent as Worker} from "../Assets/worker.svg";
import {ReactComponent as RMA} from "../Assets/rma.svg";

import {ReactComponent as DashBoard} from "../Assets/dashboard.svg";
import {ReactComponent as Home} from "../Assets/home.svg";
import {ReactComponent as Input} from "../Assets/input.svg";

import {ReactComponent as Item} from "../Assets/item.svg";
import {ReactComponent as Customer} from "../Assets/customer.svg";
import {ReactComponent as Upload} from "../Assets/upload.svg";
import {ReactComponent as Post} from "../Assets/post.svg";
import {ReactComponent as WorkerReport} from "../Assets/workerReport.svg";
import {ReactComponent as Invoice} from "../Assets/invoice.svg";
import {ReactComponent as Voucher} from "../Assets/voucher.svg";
import {ReactComponent as Kit} from "../Assets/kit.svg";
import {ReactComponent as User} from "../Assets/user.svg";
import {ReactComponent as Output} from "../Assets/output.svg";
import {ReactComponent as Logout} from "../Assets/logout.svg";
import {ReactComponent as Menu} from "../Assets/menu.svg";

import {Auths} from "../utilities/getAuthDep";

export const MunshiMenu = ({handle}) => {
    const navigate = useNavigate();
    const userMenuRef = useRef(null);

    const [user, setUser] = useState("");
    const [name, setName] = useState("");
    const [database, setDatabase] = useState("");
    const [loading] = useState(false);

    const [mobileOpen, setMobileOpen] = useState(false);
    const mobileFirstFocusRef = useRef(null);

    const auth = useMemo(() => Auths(), []);

    useEffect(() => {
        setUser(Cookies.get("user") || "");
        setName(Cookies.get("name") || "");
        setDatabase(Cookies.get("database") || "");
    }, []);

    const go = useCallback(
        (path) => {
            navigate(path);
            setMobileOpen(false); // close mobile menu after navigation
        },
        [navigate]
    );

    const initials = useMemo(() => {
        const base = (name || user || "").trim();
        if (!base) return "?";
        const parts = base.split(/\s+/).filter(Boolean);
        const first = parts[0]?.[0] || "";
        const second = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
        return (first + second).toUpperCase() || "?";
    }, [name, user]);

    const userArea = useMemo(
        () => (
            <div className="munshiUserArea flex align-items-center gap-2">
                <Avatar label={initials} shape="circle" className="munshiUserAvatar"/>
                <Button
                    type="button"
                    label={`${user}${name ? ` / ${name}` : ""}`}
                    icon="pi pi-angle-down"
                    iconPos="right"
                    className="p-button-text p-button-plain munshiDarkUserButton"
                    onClick={(e) => userMenuRef.current?.toggle(e)}
                />
                <OverlayPanel
                    ref={userMenuRef}
                    dismissable
                    showCloseIcon
                    className="munshiDarkOverlay"
                >
                    <div className="flex flex-column gap-2" style={{minWidth: "18rem"}}>
                        <div className="flex align-items-center justify-content-between gap-3">
                            <div className="flex flex-column">
                                <span className="font-bold">{name || "User"}</span>
                                <span className="text-sm text-600">{user || "-"}</span>
                            </div>
                            {database ? <Tag value={database} severity="info" /> : null}
                        </div>

                        <Divider className="my-2" />

                        <div className="flex gap-2">
                            <Button
                                type="button"
                                icon="pi pi-desktop"
                                label="Fullscreen"
                                className="p-button-outlined p-button-sm"
                                onClick={handle?.enter}
                            />
                            <Button
                                type="button"
                                icon={<Logout/>}
                                label="Logout"
                                className="p-button-danger p-button-sm"
                                onClick={() => go("/login")}
                            />
                        </div>
                    </div>
                </OverlayPanel>
            </div>
        ),
        [handle, initials, name, user, database, go]
    );

    /**
     * Desktop menu model (Menubar).
     * PrimeReact Menubar already supports keyboard navigation:
     * - Tab focuses the menubar
     * - Arrow keys navigate items/submenus
     * - Enter/Space activates
     */
    const desktopModel = useMemo(
        () => [
            {
                label: "Home",
                icon: <Home/>,
                command: () => go("/home"),
            },
            {
                label: "Transaction",
                icon: "pi pi-arrow-right-arrow-left",
                items: [
                    {label: "INVOICE", icon: <Invoice/>, command: () => go("/invoice")},
                    {
                        label: "PURCHASE",
                        icon: <Purchase/>,
                        command: () => go("/Purchase"),
                        disabled: !auth.purchase() && !auth.afterSales(),
                    },
                    {
                        label: "CASH BOOK",
                        icon: <CashBook/>,
                        command: () => go("/CashBook"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "VOUCHER",
                        icon: <Voucher/>,
                        command: () => go("/Voucher"),
                        disabled: !auth.cashier(),
                    },
                ],
            },
            {
                label: "Reports",
                icon: "pi pi-chart-bar",
                items: [
                    {label: "LEDGER", icon: <Ledger/>, command: () => go("/ledger")},
                    {
                        label: "CUSTOMER",
                        icon: <Customer/>,
                        command: () => go("/customer"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "INVENTORY",
                        icon: <Inventory/>,
                        command: () => go("/inventory"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "STOCK",
                        icon: <Stock/>,
                        command: () => go("/stock"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "ITEM",
                        icon: <Item/>,
                        command: () => go("/item-analytics"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "SUMMERY",
                        icon: <BalanceIcon/>,
                        command: () => go("/summery"),
                        disabled: !auth.all(),
                    }

                ],
            },
            {
                label: "Utilities",
                icon: "pi pi-cog",
                items: [
                    {
                        label: "POST/UNPOST",
                        icon: <Post/>,
                        command: () => go("/post"),
                        disabled: !auth.posting(),
                    },
                    {
                        label: "ITEM",
                        icon: <Item/>,
                        command: () => go("/item"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "CUSTOMER EDIT",
                        icon: <Customer/>,
                        command: () => go("/customerEdit"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "EMPLOYEE",
                        icon: <User/>,
                        command: () => go("/signUp"),
                        disabled: !auth.cashier(),
                    },
                    {label: "UPLOAD", icon: <Upload/>, command: () => go("/upload")},
                ],
            },
            {
                label: "Manufacturing",
                icon: "pi pi-warehouse",
                items: [
                    {
                        label: "KIT EDIT",
                        icon: <Kit/>,
                        command: () => go("/kit"),
                        disabled: !auth.factoryAdmin(),
                    },
                    {
                        label: "INPUT",
                        icon: <Input/>,
                        command: () => go("/mfxInput"),
                        disabled: !auth.factory(),
                    },
                    {
                        label: "PRODUCTION",
                        icon: <Output/>,
                        command: () => go("/mfxOutput"),
                        disabled: !auth.factory(),
                    },
                    {
                        label: "DASHBOARD",
                        icon: <DashBoard/>,
                        command: () => go("/dashboard"),
                        disabled: !auth.factoryAdmin(),
                    },
                ],
            },
            {
                label: "Workers",
                icon: "pi pi-warehouse",
                items: [
                    {
                        label: "WORKER EDIT",
                        icon: <Worker/>,
                        command: () => go("/worker"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "ATTENDANCE REPORT",
                        icon: <WorkerReport/>,
                        command: () => go("/workerReport"),
                        disabled: !auth.cashier(),
                    },
                ],
            },
            {
                label: "RMA",
                icon: "pi pi-wrench",
                items: [
                    {
                        label: "RMA List",
                        icon: <RMA/>,
                        command: () => go("/rma"),
                        disabled: !auth.afterSales(),
                    },
                ],
            },

        ],
        [auth, go]
    );

    /**
     * Mobile menu model (PanelMenu).
     * PanelMenu is also keyboard navigable (Tab/Arrow/Enter), and works great in a Sidebar.
     */
    const mobileModel = useMemo(
        () => [
            {
                label: "Home",
                icon: <Home/>,
                command: () => go("/home"),
            },
            {
                label: "Transaction",
                icon: "pi pi-arrow-right-arrow-left",
                items: [
                    {label: "INVOICE", icon: <Invoice/>, command: () => go("/invoice")},
                    {
                        label: "PURCHASE",
                        icon: <Purchase/>,
                        command: () => go("/Purchase"),
                        disabled: !auth.purchase() && !auth.afterSales(),
                    },
                    {
                        label: "CASH BOOK",
                        icon: <CashBook/>,
                        command: () => go("/CashBook"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "VOUCHER",
                        icon: <Voucher/>,
                        command: () => go("/Voucher"),
                        disabled: !auth.cashier(),
                    },
                ],
            },
            {
                label: "Reports",
                icon: "pi pi-chart-bar",
                items: [
                    {label: "LEDGER", icon: <Ledger/>, command: () => go("/ledger")},
                    {
                        label: "CUSTOMER",
                        icon: <Customer/>,
                        command: () => go("/customer"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "INVENTORY",
                        icon: <Inventory/>,
                        command: () => go("/inventory"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "STOCK",
                        icon: <Stock/>,
                        command: () => go("/stock"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "ITEM",
                        icon: <Item/>,
                        command: () => go("/item-analytics"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "SUMMERY",
                        icon: <BalanceIcon/>,
                        command: () => go("/summery"),
                        disabled: !auth.all(),
                    },

                ],
            },
            {
                label: "Utilities",
                icon: "pi pi-cog",
                items: [
                    {
                        label: "POST/UNPOST",
                        icon: <Post/>,
                        command: () => go("/post"),
                        disabled: !auth.posting(),
                    },
                    {
                        label: "ITEM",
                        icon: <Item/>,
                        command: () => go("/item"),
                        disabled: !auth.inventory(),
                    },
                    {
                        label: "CUSTOMER EDIT",
                        icon: <Customer/>,
                        command: () => go("/customerEdit"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "EMPLOYEE",
                        icon: <User/>,
                        command: () => go("/signUp"),
                        disabled: !auth.cashier(),
                    },
                    {label: "UPLOAD", icon: <Upload/>, command: () => go("/upload")},
                ],
            },
            {
                label: "Manufacturing",
                icon: "pi pi-warehouse",
                items: [
                    {
                        label: "KIT EDIT",
                        icon: <Kit/>,
                        command: () => go("/kit"),
                        disabled: !auth.factoryAdmin(),
                    },
                    {
                        label: "INPUT",
                        icon: <Input/>,
                        command: () => go("/mfxInput"),
                        disabled: !auth.factory(),
                    },
                    {
                        label: "PRODUCTION",
                        icon: <Output/>,
                        command: () => go("/mfxOutput"),
                        disabled: !auth.factory(),
                    },
                    {
                        label: "DASHBOARD",
                        icon: <DashBoard/>,
                        command: () => go("/dashboard"),
                        disabled: !auth.factoryAdmin(),
                    },
                ],
            },
            {
                label: "Workers",
                icon: "pi pi-warehouse",
                items: [
                    {
                        label: "WORKER EDIT",
                        icon: <Worker/>,
                        command: () => go("/worker"),
                        disabled: !auth.cashier(),
                    },
                    {
                        label: "ATTENDANCE REPORT",
                        icon: <WorkerReport/>,
                        command: () => go("/workerReport"),
                        disabled: !auth.cashier(),
                    },
                ],
            },
    //    {
    //        label: "RMA",
    //        icon: "pi pi-wrench",
    //        items: [
    //            {
    //                label: "RMA List",
    //                icon: <RMA/>,
    //                command: () => go("/rma"),
    //                disabled: !auth.afterSales(),
    //            },
    //        ],
    //    },
            {
                label: "Balance Sheet",
                icon: "pi pi-wrench",
                items: [
                    {
                        label: "RMA List",
                        icon: <RMA/>,
                        command: () => go("/balance-sheet"),
                        disabled: !auth.afterSales(),
                    },
                ],
            },
        ],
        [auth, go]
    );

    useEffect(() => {
        if (!mobileOpen) return;
        const id = window.setTimeout(() => mobileFirstFocusRef.current?.focus?.(), 0);
        return () => window.clearTimeout(id);
    }, [mobileOpen]);

    const onMobileKeyDown = useCallback((e) => {
        if (e.key === "Escape") setMobileOpen(false);
    }, []);

    if (loading) return null;

    return (
        <>
            <Toolbar
                className="munshiTopBar munshiTopBar--dark shadow-2"
                left={
                    <div className="munshiTopBarInner w-full flex align-items-center justify-content-between gap-2">
                        <div className="munshiLeftCluster flex align-items-center gap-2">
                            <Button
                                type="button"
                                className="munshiHamburger p-button-text p-button-plain"
                                onClick={() => setMobileOpen(true)}
                                aria-label="Open menu"
                                ref={mobileFirstFocusRef}
                            >
                                <Menu/>
                            </Button>

                            <Menubar
                                model={desktopModel}
                                className="munshiMenubar munshiMenubar--dark"
                            />
                        </div>

                        {userArea}
                    </div>
                }
            />

            <Sidebar
                visible={mobileOpen}
                onHide={() => setMobileOpen(false)}
                position="left"
                className="munshiMobileSidebar munshiMobileSidebar--dark"
                onKeyDown={onMobileKeyDown}
            >
                <div className="munshiMobileHeader flex align-items-center justify-content-between mb-3">
                    <div className="flex flex-column">
                        <div className="munshiMobileTitle">Menu</div>
                        <div className="munshiMobileSubTitle">{database ? `DB: ${database}` : ""}</div>
                    </div>
                    <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-text p-button-plain munshiIconButton"
                        onClick={() => setMobileOpen(false)}
                        aria-label="Close menu"
                    />
                </div>

                <PanelMenu model={mobileModel} className="munshiPanelMenu munshiPanelMenu--dark" />

                <Divider className="my-3" />

                <div className="flex flex-column gap-2">
                    <Button
                        type="button"
                        icon="pi pi-desktop"
                        label="Fullscreen"
                        className="p-button-outlined"
                        onClick={handle?.enter}
                    />
                    <Button
                        type="button"
                        icon={<Logout/>}
                        label="Logout"
                        className="p-button-danger"
                        onClick={() => go("/login")}
                    />
                </div>
            </Sidebar>
        </>
    );
};